import React, { useState, useEffect } from 'react';
import { api } from '../../lib/api';

interface SupportEmail {
  id: string;
  from_email: string;
  from_name?: string;
  to_email: string;
  subject?: string;
  text_body?: string;
  html_body?: string;
  stripped_text_body?: string;
  stripped_html_body?: string;
  status: string;
  is_read: boolean;
  is_archived: boolean;
  attachment_count: number;
  received_at: string;
  read_at?: string;
  replied_at?: string;
  archived_at?: string;
}

// Sanitize HTML email content to remove cid: references that browsers can't handle
const sanitizeEmailHtml = (html: string): string => {
  if (!html) return html;
  
  let sanitized = html;
  
  // Replace img tags with cid: sources with placeholder text
  // Matches: <img src="cid:..." alt="..."> or <img src='cid:...' alt='...'> or <img src=cid:...>
  sanitized = sanitized.replace(
    /<img[^>]*src\s*=\s*["']?cid:[^"'>\s]+["']?[^>]*>/gi,
    (match) => {
      // Extract alt text if available
      const altMatch = match.match(/alt\s*=\s*["']([^"']+)["']/i);
      const alt = altMatch ? altMatch[1] : 'Embedded image';
      return `<span class="inline-block px-2 py-1 bg-gray-100 border border-gray-300 rounded text-xs text-gray-600">[${alt}]</span>`;
    }
  );
  
  // Remove cid: references from style attributes (background images, etc.)
  sanitized = sanitized.replace(
    /style\s*=\s*["']([^"']*)url\(['"]?cid:[^'"]+['"]?\)([^"']*)["']/gi,
    (match, before, after) => {
      const cleaned = (before + after).trim();
      return cleaned ? `style="${cleaned}"` : '';
    }
  );
  
  // Also handle cid: in inline styles without quotes
  sanitized = sanitized.replace(/url\(['"]?cid:[^'")]+['"]?\)/gi, '');
  
  return sanitized;
};

export const SupportInboxTab: React.FC = () => {
  const [emails, setEmails] = useState<SupportEmail[]>([]);
  const [selectedEmail, setSelectedEmail] = useState<SupportEmail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'sales' | 'support'>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'new' | 'read' | 'replied' | 'archived'>('all');
  const [replySubject, setReplySubject] = useState('');
  const [replyBody, setReplyBody] = useState('');
  const [isReplying, setIsReplying] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [total, setTotal] = useState(0);
  const [diagnostics, setDiagnostics] = useState<any>(null);
  const [showDiagnostics, setShowDiagnostics] = useState(false);
  const [showTestEmailModal, setShowTestEmailModal] = useState(false);
  const [testEmailAddress, setTestEmailAddress] = useState('');
  const [isSendingTest, setIsSendingTest] = useState(false);

  useEffect(() => {
    console.log('SupportInboxTab mounted, loading emails...');
    loadEmails();
    loadDiagnostics();
    loadWebhookInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, statusFilter]);

  const [webhookInfo, setWebhookInfo] = useState<any>(null);

  const loadDiagnostics = async () => {
    try {
      const response = await api.serverAdmin.getSupportEmailsDiagnostics();
      setDiagnostics(response.data);
      console.log('Support emails diagnostics:', response.data);
    } catch (error: any) {
      console.error('Failed to load diagnostics:', error);
    }
  };

  const loadWebhookInfo = async () => {
    try {
      const response = await api.serverAdmin.getWebhookInfo();
      setWebhookInfo(response.data);
    } catch (error: any) {
      console.error('Failed to load webhook info:', error);
    }
  };

  const loadEmails = async () => {
    try {
      setIsLoading(true);
      setMessage(null);
      
      const params: any = {
        limit: 100
      };
      
      if (filter === 'sales') {
        params.to_email = 'sales@feedbackgate.com';
      } else if (filter === 'support') {
        params.to_email = 'support@feedbackgate.com';
      }
      
      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }
      
      console.log('Loading support emails with params:', params);
      const response = await api.serverAdmin.getSupportEmails(params);
      console.log('Support emails response:', response.data);
      
      setEmails(response.data.emails || []);
      setTotal(response.data.total || 0);
      
      if (response.data.total === 0) {
        setMessage({ 
          type: 'error', 
          text: 'No support emails found in database. Check if Postmark webhook is configured and emails are being received.' 
        });
      }
    } catch (error: any) {
      console.error('Failed to load support emails:', error);
      console.error('Error details:', error.response?.data);
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.detail || error.message || 'Failed to load support emails' 
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleEmailClick = async (email: SupportEmail) => {
    setSelectedEmail(email);
    setReplySubject(email.subject?.startsWith('Re:') || email.subject?.startsWith('RE:') ? email.subject : `Re: ${email.subject || ''}`);
    setReplyBody('');
    
    // Mark as read if unread
    if (!email.is_read) {
      try {
        await api.serverAdmin.markSupportEmailRead(email.id);
        setEmails(prev => prev.map(e => 
          e.id === email.id ? { ...e, is_read: true, status: 'read' } : e
        ));
        if (selectedEmail?.id === email.id) {
          setSelectedEmail({ ...email, is_read: true, status: 'read' });
        }
      } catch (error) {
        console.error('Failed to mark as read:', error);
      }
    }
  };

  const handleReply = async () => {
    if (!selectedEmail || !replyBody.trim()) {
      setMessage({ type: 'error', text: 'Please enter a reply message' });
      return;
    }
    
    try {
      setIsReplying(true);
      setMessage(null);
      
      const result = await api.serverAdmin.replyToSupportEmail(selectedEmail.id, {
        subject: replySubject,
        body: replyBody,
        html_body: replyBody.replace(/\n/g, '<br>')
      });
      
      if (result.data.success) {
        // Mark as replied
        setEmails(prev => prev.map(e => 
          e.id === selectedEmail.id ? { ...e, status: 'replied', replied_at: new Date().toISOString() } : e
        ));
        
        if (selectedEmail) {
          setSelectedEmail({ ...selectedEmail, status: 'replied', replied_at: new Date().toISOString() });
        }
        
        setReplyBody('');
        setMessage({ type: 'success', text: 'Reply sent successfully!' });
      }
    } catch (error: any) {
      console.error('Failed to send reply:', error);
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.detail || 'Failed to send reply' 
      });
    } finally {
      setIsReplying(false);
    }
  };

  const handleArchive = async (email: SupportEmail) => {
    try {
      await api.serverAdmin.archiveSupportEmail(email.id);
      setEmails(prev => prev.map(e => 
        e.id === email.id ? { ...e, is_archived: true, status: 'archived' } : e
      ));
      
      if (selectedEmail?.id === email.id) {
        setSelectedEmail({ ...email, is_archived: true, status: 'archived' });
      }
      
      setMessage({ type: 'success', text: 'Email archived' });
    } catch (error: any) {
      console.error('Failed to archive email:', error);
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.detail || 'Failed to archive email' 
      });
    }
  };

  const handleSendTestEmail = async () => {
    if (!testEmailAddress || !testEmailAddress.includes('@')) {
      setMessage({ type: 'error', text: 'Please enter a valid email address' });
      return;
    }
    
    try {
      setIsSendingTest(true);
      setMessage(null);
      
      const result = await api.serverAdmin.sendTestEmail({
        to_email: testEmailAddress,
        subject: 'Test Email from FeedbackGate Server Admin',
        html_body: '<p>This is a test email from the FeedbackGate server admin panel.</p><p>If you received this email, your email configuration is working correctly.</p>',
        text_body: 'This is a test email from the FeedbackGate server admin panel.\n\nIf you received this email, your email configuration is working correctly.'
      });
      
      if (result.data.success) {
        setMessage({ type: 'success', text: `Test email sent successfully to ${testEmailAddress}! Check your inbox.` });
        setTestEmailAddress('');
        setShowTestEmailModal(false);
      }
    } catch (error: any) {
      console.error('Failed to send test email:', error);
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.detail || 'Failed to send test email' 
      });
    } finally {
      setIsSendingTest(false);
    }
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'new': return 'bg-blue-100 text-blue-800';
      case 'read': return 'bg-gray-100 text-gray-800';
      case 'replied': return 'bg-green-100 text-green-800';
      case 'archived': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-medium text-gray-900">Support Inbox</h3>
          <p className="text-sm text-gray-600 mt-1">
            {total} total email{total !== 1 ? 's' : ''}
            {diagnostics && (
              <span className="ml-2 text-xs text-gray-500">
                (DB: {diagnostics.total_inbound_emails} total, {diagnostics.total_support_emails} support)
              </span>
            )}
          </p>
        </div>
        <div className="flex space-x-4">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value as any)}
            className="text-sm border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="all">All Emails</option>
            <option value="sales">sales@feedbackgate.com</option>
            <option value="support">support@feedbackgate.com</option>
          </select>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as any)}
            className="text-sm border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="all">All Status</option>
            <option value="new">New</option>
            <option value="read">Read</option>
            <option value="replied">Replied</option>
            <option value="archived">Archived</option>
          </select>
          <button
            onClick={loadEmails}
            className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            Refresh
          </button>
          <button
            onClick={() => {
              setShowDiagnostics(!showDiagnostics);
              if (!showDiagnostics) {
                loadDiagnostics();
              }
            }}
            className="px-4 py-2 bg-gray-600 text-white text-sm font-medium rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500"
          >
            {showDiagnostics ? 'Hide' : 'Show'} Diagnostics
          </button>
          <button
            onClick={() => setShowTestEmailModal(true)}
            className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
          >
            Send Test Email
          </button>
        </div>
      </div>

      {/* Test Email Modal */}
      {showTestEmailModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Send Test Email
              </h3>
              <div className="mt-2 px-7 py-3">
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={testEmailAddress}
                    onChange={(e) => setTestEmailAddress(e.target.value)}
                    placeholder="your-email@example.com"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
              <div className="flex items-center px-4 py-3 space-x-3">
                <button
                  onClick={handleSendTestEmail}
                  disabled={isSendingTest || !testEmailAddress}
                  className="px-4 py-2 bg-green-600 text-white text-base font-medium rounded-md shadow-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSendingTest ? 'Sending...' : 'Send Test Email'}
                </button>
                <button
                  onClick={() => {
                    setShowTestEmailModal(false);
                    setTestEmailAddress('');
                  }}
                  disabled={isSendingTest}
                  className="px-4 py-2 bg-gray-300 text-gray-800 text-base font-medium rounded-md shadow-sm hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Diagnostics Panel */}
      {showDiagnostics && diagnostics && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <h4 className="font-medium text-yellow-900 mb-3">Database Diagnostics</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <span className="text-yellow-700 font-medium">Total Inbound Emails:</span>
              <span className="ml-2 text-yellow-900">{diagnostics.total_inbound_emails}</span>
            </div>
            <div>
              <span className="text-yellow-700 font-medium">Support Emails (flagged):</span>
              <span className="ml-2 text-yellow-900">{diagnostics.total_support_emails}</span>
            </div>
            <div>
              <span className="text-yellow-700 font-medium">sales@ emails:</span>
              <span className="ml-2 text-yellow-900">{diagnostics.sales_emails || 0}</span>
            </div>
            <div>
              <span className="text-yellow-700 font-medium">support@ emails:</span>
              <span className="ml-2 text-yellow-900">{diagnostics.support_addr_emails || 0}</span>
            </div>
          </div>
          
          {diagnostics.recent_emails && diagnostics.recent_emails.length > 0 && (
            <div className="mt-4">
              <h5 className="font-medium text-yellow-900 mb-2">Recent Emails in Database:</h5>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {diagnostics.recent_emails.map((email: any, idx: number) => (
                  <div key={idx} className="text-xs bg-white p-2 rounded border border-yellow-200">
                    <div className="flex justify-between">
                      <span className="font-medium">{email.to_email}</span>
                      <span className={email.is_support_email ? 'text-green-600' : 'text-gray-500'}>
                        {email.is_support_email ? '✓ Support' : 'Not Support'}
                      </span>
                    </div>
                    <div className="text-gray-600 truncate">{email.subject || '(No Subject)'}</div>
                    <div className="text-gray-500 text-xs">{email.from_email}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {diagnostics.recent_webhooks && diagnostics.recent_webhooks.length > 0 && (
            <div className="mt-4">
              <h5 className="font-medium text-yellow-900 mb-2">Recent Webhook Events:</h5>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {diagnostics.recent_webhooks.map((webhook: any, idx: number) => (
                  <div key={idx} className={`text-xs p-2 rounded border ${
                    webhook.processed_successfully 
                      ? 'bg-green-50 border-green-200' 
                      : 'bg-red-50 border-red-200'
                  }`}>
                    <div className="flex justify-between">
                      <span>{webhook.event_type}</span>
                      <span className={webhook.processed_successfully ? 'text-green-600' : 'text-red-600'}>
                        {webhook.processed_successfully ? '✓ Success' : '✗ Failed'}
                      </span>
                    </div>
                    {webhook.error_message && (
                      <div className="text-red-600 text-xs mt-1">{webhook.error_message}</div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {diagnostics.total_inbound_emails === 0 && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded">
              <p className="text-sm text-red-800 font-medium">⚠️ No emails found in database</p>
              <p className="text-xs text-red-700 mt-1">
                This could mean:
                <ul className="list-disc list-inside mt-1 space-y-1">
                  <li>Postmark webhook is not configured correctly</li>
                  <li>Webhook URL is not receiving emails</li>
                  <li>Database migration has not been run</li>
                  <li>Emails are being received but not processed</li>
                </ul>
              </p>
            </div>
          )}
          
          {diagnostics.total_inbound_emails > 0 && diagnostics.total_support_emails === 0 && (
            <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded">
              <p className="text-sm text-yellow-800 font-medium">⚠️ Emails found but not marked as support emails</p>
              <p className="text-xs text-yellow-700 mt-1 mb-2">
                There are {diagnostics.total_inbound_emails} emails in the database, but none are marked as support emails.
                This might be because existing emails were processed before the support email detection was fixed.
              </p>
              <button
                onClick={async () => {
                  try {
                    const response = await api.serverAdmin.fixExistingSupportEmails();
                    alert(`Fixed ${response.data.fixed_count} emails! Refresh to see them.`);
                    loadDiagnostics();
                    loadEmails();
                  } catch (error: any) {
                    console.error('Failed to fix emails:', error);
                    alert('Failed to fix emails: ' + (error.response?.data?.detail || error.message));
                  }
                }}
                className="px-3 py-1 bg-yellow-600 text-white text-sm rounded hover:bg-yellow-700"
              >
                Fix Existing Emails
              </button>
            </div>
          )}

          {/* Webhook Status */}
          {diagnostics.webhook_stats && (
            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded">
              <p className="text-sm text-blue-800 font-medium">📡 Webhook Status</p>
              <div className="text-xs text-blue-700 mt-2 space-y-1">
                <p>Postmark webhooks (last 7 days): {diagnostics.webhook_stats.total_postmark_webhooks_last_7d}</p>
                <p>Inbound webhooks received: {diagnostics.webhook_stats.total_inbound_webhooks}</p>
                {diagnostics.webhook_stats.most_recent_webhook && (
                  <p>Most recent webhook: {new Date(diagnostics.webhook_stats.most_recent_webhook).toLocaleString()}</p>
                )}
                {diagnostics.webhook_stats.most_recent_inbound && (
                  <p>Most recent inbound: {new Date(diagnostics.webhook_stats.most_recent_inbound).toLocaleString()}</p>
                )}
                {!diagnostics.webhook_stats.most_recent_webhook && (
                  <p className="text-red-600 font-medium">⚠️ No webhooks received - check Postmark configuration</p>
                )}
              </div>
              {diagnostics.warnings && diagnostics.warnings.length > 0 && (
                <div className="mt-2">
                  {diagnostics.warnings.map((warning: string, idx: number) => (
                    <p key={idx} className="text-xs text-red-700 mt-1">⚠️ {warning}</p>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Webhook Configuration Info */}
          {webhookInfo && (
            <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded">
              <p className="text-sm text-green-800 font-medium">🔗 Postmark Webhook Configuration</p>
              <div className="text-xs text-green-700 mt-2 space-y-2">
                <div>
                  <p className="font-medium">Inbound Webhook URL:</p>
                  <code className="bg-white px-2 py-1 rounded text-xs break-all block mt-1">{webhookInfo.inbound_webhook_url}</code>
                </div>
                <div>
                  <p className="font-medium">Webhook Secret:</p>
                  <p>{webhookInfo.webhook_secret_configured ? '✓ Configured' : '✗ Not configured - set POSTMARK_WEBHOOK_SECRET'}</p>
                </div>
                <div className="mt-2">
                  <p className="font-medium">Configuration Steps:</p>
                  <ol className="list-decimal list-inside space-y-1 mt-1">
                    {Object.values(webhookInfo.instructions).map((step: any, idx: number) => (
                      <li key={idx} className="text-xs">{step}</li>
                    ))}
                  </ol>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Message Display */}
      {message && (
        <div className={`p-4 rounded-md ${
          message.type === 'success' 
            ? 'bg-green-50 text-green-800 border border-green-200' 
            : 'bg-red-50 text-red-800 border border-red-200'
        }`}>
          {message.text}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Email List */}
        <div className="lg:col-span-1 bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="p-4 border-b border-gray-200 bg-gray-50">
            <h4 className="font-medium text-gray-900">Inbox</h4>
          </div>
          <div className="divide-y divide-gray-200 max-h-[700px] overflow-y-auto">
            {isLoading ? (
              <div className="p-4 text-center text-gray-500">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
                <p className="mt-2 text-sm">Loading emails...</p>
              </div>
            ) : emails.length === 0 ? (
              <div className="p-4 text-center text-gray-500">
                <p className="text-sm">No emails found</p>
              </div>
            ) : (
              emails.map((email) => (
                <div
                  key={email.id}
                  onClick={() => handleEmailClick(email)}
                  className={`p-4 cursor-pointer hover:bg-gray-50 transition-colors ${
                    selectedEmail?.id === email.id ? 'bg-blue-50 border-l-4 border-blue-500' : ''
                  } ${!email.is_read ? 'bg-blue-50' : ''}`}
                >
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm truncate ${!email.is_read ? 'font-semibold' : 'font-medium'} text-gray-900`}>
                        {email.from_name || email.from_email}
                      </p>
                      <p className="text-xs text-gray-500 truncate">{email.to_email}</p>
                    </div>
                    <div className="flex items-center space-x-1 ml-2">
                      {!email.is_read && (
                        <span className="w-2 h-2 bg-blue-600 rounded-full"></span>
                      )}
                      <span className={`text-xs px-2 py-0.5 rounded ${getStatusBadgeColor(email.status)}`}>
                        {email.status}
                      </span>
                    </div>
                  </div>
                  <p className="text-sm text-gray-700 truncate mb-1">
                    {email.subject || '(No Subject)'}
                  </p>
                  <p className="text-xs text-gray-500">
                    {new Date(email.received_at).toLocaleString()}
                  </p>
                  {email.attachment_count > 0 && (
                    <p className="text-xs text-blue-600 mt-1">
                      📎 {email.attachment_count} attachment{email.attachment_count !== 1 ? 's' : ''}
                    </p>
                  )}
                </div>
              ))
            )}
          </div>
        </div>

        {/* Email Viewer & Reply */}
        <div className="lg:col-span-2 bg-white border border-gray-200 rounded-lg flex flex-col" style={{ minHeight: '700px' }}>
          {selectedEmail ? (
            <div className="flex flex-col h-full">
              {/* Email Header */}
              <div className="p-6 border-b border-gray-200 flex-shrink-0">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1">
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      {selectedEmail.subject || '(No Subject)'}
                    </h3>
                    <div className="space-y-1 text-sm text-gray-600">
                      <p>
                        <span className="font-medium">From:</span> {selectedEmail.from_name || ''} &lt;{selectedEmail.from_email}&gt;
                      </p>
                      <p>
                        <span className="font-medium">To:</span> {selectedEmail.to_email}
                      </p>
                      <p>
                        <span className="font-medium">Received:</span> {new Date(selectedEmail.received_at).toLocaleString()}
                      </p>
                      {selectedEmail.replied_at && (
                        <p className="text-green-600">
                          <span className="font-medium">Replied:</span> {new Date(selectedEmail.replied_at).toLocaleString()}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex space-x-2 ml-4">
                    {!selectedEmail.is_archived && (
                      <button
                        onClick={() => handleArchive(selectedEmail)}
                        className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500"
                      >
                        Archive
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Email Body */}
              <div className="flex-1 p-6 overflow-y-auto bg-gray-50">
                {selectedEmail.html_body ? (
                  <div 
                    className="prose max-w-none bg-white p-4 rounded border border-gray-200"
                    dangerouslySetInnerHTML={{ __html: sanitizeEmailHtml(selectedEmail.html_body) }}
                  />
                ) : selectedEmail.stripped_html_body ? (
                  <div 
                    className="prose max-w-none bg-white p-4 rounded border border-gray-200"
                    dangerouslySetInnerHTML={{ __html: sanitizeEmailHtml(selectedEmail.stripped_html_body) }}
                  />
                ) : selectedEmail.stripped_text_body ? (
                  <pre className="whitespace-pre-wrap text-sm text-gray-700 bg-white p-4 rounded border border-gray-200">
                    {selectedEmail.stripped_text_body}
                  </pre>
                ) : (
                  <pre className="whitespace-pre-wrap text-sm text-gray-700 bg-white p-4 rounded border border-gray-200">
                    {selectedEmail.text_body || '(No content)'}
                  </pre>
                )}
                
                {selectedEmail.attachment_count > 0 && (
                  <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded">
                    <p className="text-sm text-yellow-800">
                      ⚠️ This email has {selectedEmail.attachment_count} attachment{selectedEmail.attachment_count !== 1 ? 's' : ''}. 
                      View attachments in Postmark dashboard.
                    </p>
                  </div>
                )}
              </div>

              {/* Reply Section */}
              {selectedEmail.status !== 'archived' && (
                <div className="p-6 border-t border-gray-200 bg-white flex-shrink-0">
                  <h4 className="font-medium text-gray-900 mb-4">Reply</h4>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Subject
                      </label>
                      <input
                        type="text"
                        value={replySubject}
                        onChange={(e) => setReplySubject(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Message
                      </label>
                      <textarea
                        value={replyBody}
                        onChange={(e) => setReplyBody(e.target.value)}
                        rows={6}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Type your reply..."
                      />
                    </div>
                    <button
                      onClick={handleReply}
                      disabled={isReplying || !replyBody.trim()}
                      className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isReplying ? 'Sending...' : 'Send Reply'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center justify-center h-full p-12">
              <div className="text-center text-gray-500">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <p className="mt-4 text-sm">Select an email to view and reply</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

